import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthContextType } from '../types';
import apiClient from '../services/apiClient';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthStrategy = 'local' | 'cognito';

interface AuthConfig {
  strategy: AuthStrategy;
  cognito: {
    enabled: boolean;
    region: string | null;
    userPoolId: string | null;
    clientId: string | null;
    hostedUiBaseUrl: string | null;
  };
}

const SECURE_APP_URL = 'https://d2td37b66hzwfg.cloudfront.net';
const USER_SCOPED_STORAGE_KEYS = [
  'awsCloudAccountId',
  'awsConfig',
  'awsConnected',
  'awsData',
  'cloudConnections',
  'gcpCloudAccountId',
  'gcpProjectId',
  'selectedSubscription',
  'userPlan'
];

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);

  const authStrategy = authConfig?.strategy || 'local';

  const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    USER_SCOPED_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
    window.dispatchEvent(new Event('cloud:clear'));
    setUser(null);
    setIsAuthenticated(false);
  };

  useEffect(() => {
    let isMounted = true;

    const restoreSession = async () => {
      try {
        const configResponse = await apiClient.get('/auth/config');
        if (isMounted) setAuthConfig(configResponse.data);
      } catch (error) {
        if (isMounted) {
          setAuthConfig({
            strategy: 'local',
            cognito: {
              enabled: false,
              region: null,
              userPoolId: null,
              clientId: null,
              hostedUiBaseUrl: null
            }
          });
        }
      }

      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (!token) {
        if (isMounted) setLoading(false);
        return;
      }

      if (savedUser && isMounted) {
        try {
          setUser(JSON.parse(savedUser));
        } catch (error) {
          localStorage.removeItem('user');
        }
      }

      try {
        const response = await apiClient.get('/auth/me');
        if (!isMounted) return;

        const nextUser = response.data.user;
        localStorage.setItem('user', JSON.stringify(nextUser));
        setUser(nextUser);
        setIsAuthenticated(true);
      } catch (error) {
        if (isMounted) clearSession();
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    const handleUnauthorized = () => {
      clearSession();
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    restoreSession();

    return () => {
      isMounted = false;
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  const getCognitoConfig = () => {
    const cognito = authConfig?.cognito;
    if (!cognito?.clientId || !cognito.hostedUiBaseUrl) {
      throw new Error('Configuração do Cognito incompleta.');
    }

    return {
      clientId: cognito.clientId,
      hostedUiBaseUrl: cognito.hostedUiBaseUrl.replace(/\/+$/, ''),
      redirectUri: `${window.location.origin}/auth/callback`
    };
  };

  const exchangeCognitoCode = async (code: string, state: string) => {
    const expectedState = sessionStorage.getItem('cognito_oauth_state');
    const codeVerifier = sessionStorage.getItem('cognito_code_verifier');

    if (!expectedState || expectedState !== state || !codeVerifier) {
      throw new Error('Estado de autenticação inválido. Tente entrar novamente.');
    }

    const { clientId, hostedUiBaseUrl, redirectUri } = getCognitoConfig();
    const response = await fetch(`${hostedUiBaseUrl}/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code,
        redirect_uri: redirectUri,
        code_verifier: codeVerifier
      })
    });

    if (!response.ok) {
      throw new Error('Falha ao trocar o código do Cognito por token.');
    }

    const tokens = await response.json();
    if (!tokens.id_token) {
      throw new Error('Cognito não retornou id_token.');
    }

    sessionStorage.removeItem('cognito_oauth_state');
    sessionStorage.removeItem('cognito_code_verifier');
    localStorage.setItem('token', tokens.id_token);

    const meResponse = await apiClient.get('/auth/me');
    const nextUser = meResponse.data.user;

    localStorage.setItem('user', JSON.stringify(nextUser));
    setUser(nextUser);
    setIsAuthenticated(true);
  };

  const login = async (email: string, password: string) => {
    try {
      if (
        authStrategy === 'cognito' &&
        window.location.protocol !== 'https:' &&
        window.location.hostname !== 'localhost'
      ) {
        throw new Error(`Acesse pela URL segura para entrar: ${SECURE_APP_URL}`);
      }

      console.log('Tentando login com:', { email });
      const response = await apiClient.post('/auth/login', {
        email,
        password
      });
      if (response.data?.challenge === 'NEW_PASSWORD_REQUIRED') {
        return {
          success: false,
          challenge: response.data.challenge,
          session: response.data.session,
          email: response.data.email || email,
          message: response.data.message
        };
      }
      
      console.log('Resposta do login:', response.data);
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      setUser(user);
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (error: any) {
      console.error('Erro no login:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || error.message || 'Erro ao fazer login' 
      };
    }
  };

  const completeFirstAccess = async (email: string, newPassword: string, session: string) => {
    try {
      const response = await apiClient.post('/auth/complete-first-access', { email, newPassword, session });
      const { token, user: nextUser } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(nextUser));
      setUser(nextUser);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Não foi possível concluir o primeiro acesso.' };
    }
  };

  const completeCognitoLogin = async (code: string, state: string) => {
    try {
      await exchangeCognitoCode(code, state);
      return { success: true };
    } catch (error: any) {
      clearSession();
      return {
        success: false,
        message: error.message || 'Erro ao concluir login com Cognito'
      };
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await apiClient.post('/auth/change-password', {
        currentPassword,
        newPassword
      });

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erro ao alterar senha'
      };
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      const response = await apiClient.post('/auth/forgot-password', { email });

      return {
        success: true,
        message: response.data?.message || 'Se o e-mail estiver cadastrado, enviaremos um código de recuperação.'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erro ao solicitar recuperação de senha'
      };
    }
  };

  const confirmForgotPassword = async (email: string, code: string, newPassword: string) => {
    try {
      const response = await apiClient.post('/auth/confirm-forgot-password', {
        email,
        code,
        newPassword
      });

      return {
        success: true,
        message: response.data?.message || 'Senha redefinida com sucesso.'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erro ao redefinir senha'
      };
    }
  };

  const logout = () => {
    clearSession();
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    login,
    completeFirstAccess,
    changePassword,
    forgotPassword,
    confirmForgotPassword,
    completeCognitoLogin,
    logout,
    loading,
    authStrategy
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
